import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Shield,
  Clock,
  Heart,
  Target,
  Award,
  ChevronRight,
  CheckCircle2,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export const About = () => {
  const { t } = useLanguage();

  const values = [
    { icon: Shield, ...t.about.values.security },
    { icon: Clock, ...t.about.values.reliability },
    { icon: Heart, ...t.about.values.service },
    { icon: Target, ...t.about.values.quality },
  ];

  return (
    <div className="overflow-hidden pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeUp} viewport={{ once: true }}>
              <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
                {t.about.subtitle}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                {t.about.title1}{' '}
                <span className="text-[#D4AF37]">{t.about.title2}</span>
              </h1>
              <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8">
                {t.about.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://mcdgroup.de/buchungssystem/index.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-8 py-4 rounded-full font-semibold hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
                  data-testid="about-book-btn"
                >
                  {t.nav.bookNow}
                  <ChevronRight className="w-5 h-5" />
                </a>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-8 py-4 rounded-full font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                  data-testid="about-contact-btn"
                >
                  {t.about.contactBtn}
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?q=80&w=2070&auto=format&fit=crop"
                  alt="ParkingDUS Team"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center">
                    <Award className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">10+</div>
                    <div className="text-white/60 text-sm">{t.about.yearsExp}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            {...fadeUp}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-16"
          >
            <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
              {t.about.missionSubtitle}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              {t.about.missionTitle}
            </h2>
            <p className="text-white/60 text-base md:text-lg leading-relaxed">
              {t.about.missionDesc}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#D4AF37]/30 transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                  <value.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            {...fadeUp}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
              {t.about.historySubtitle}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              {t.about.historyTitle}
            </h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/10 hidden md:block" />

            <div className="space-y-12 md:space-y-0">
              {t.about.timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className={`relative md:flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="md:w-1/2 md:px-12">
                    <div className={`${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <span className="text-[#D4AF37] text-3xl font-bold">{item.year}</span>
                      <h3 className="text-xl font-semibold text-white mt-2 mb-3">{item.title}</h3>
                      <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                  
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#D4AF37] border-4 border-[#050505]" />
                  
                  <div className="md:w-1/2" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=1974&auto=format&fit=crop"
                    alt="Team Member"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-[3/4] rounded-2xl overflow-hidden mt-8">
                  <img
                    src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1976&auto=format&fit=crop"
                    alt="Team Member"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>

            <motion.div {...fadeUp} viewport={{ once: true }} className="order-1 lg:order-2">
              <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
                {t.about.teamSubtitle}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                {t.about.teamTitle1}{' '}
                <span className="text-[#D4AF37]">{t.about.teamTitle2}</span>
              </h2>
              <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8">
                {t.about.teamDesc}
              </p>

              <ul className="space-y-4">
                {t.about.teamFeatures.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-white/80 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            {...fadeUp}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[#0A0A0A] to-[#121212] border border-white/10 rounded-3xl p-8 md:p-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
              {t.about.ctaTitle}
            </h2>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-8">
              {t.about.ctaDesc}
            </p>
            <a
              href="https://mcdgroup.de/buchungssystem/index.php"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-10 py-5 rounded-full font-semibold text-lg hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
              data-testid="about-cta-btn"
            >
              {t.about.ctaBtn}
              <ChevronRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
